import { Link } from "react-router-dom";
import {
  ArrowRightIcon,
  CheckIcon,
  LockIcon,
  PlugIcon,
  CodeIcon,
  TerminalIcon,
  RefreshIcon,
} from "../components/Icons";
import { integrations } from "../components/IntegrationLogos";
import { GridPattern } from "../components/Patterns";
import { usePageMeta } from "../seo";

const capabilities = [
  {
    Icon: LockIcon,
    title: "Credentials encrypted at rest",
    body:
      "Mailbox passwords and tokens, WhatsApp and Acefone tokens, store tokens and AI keys are encrypted before they're stored. Only owners and admins can connect or change them.",
  },
  {
    Icon: RefreshIcon,
    title: "How messages arrive",
    body:
      "Connected mailboxes are checked every 30 seconds over IMAP (Gmail and Microsoft 365 sign in with OAuth; other providers use an app password), and forwarded email is picked up within seconds. WhatsApp messages and Acefone calls arrive by webhook as they happen.",
  },
  {
    Icon: CodeIcon,
    title: "Your accounts, your bills",
    body:
      "Replies go out through your own mailbox or SMTP server, WhatsApp through your Meta account, calls through your Acefone account and AI through your own OpenAI or Anthropic key.",
  },
  {
    Icon: TerminalIcon,
    title: "API and signed webhooks",
    body:
      "Read and create tickets, customers and companies with a scoped API key. Get a signed POST when a ticket is created, updated or solved, or a message is added.",
  },
];

export default function Integrations() {
  usePageMeta("/integrations");
  return (
    <>
      <section className="relative overflow-hidden bg-ink-900 text-ink-100">
        <div className="pointer-events-none absolute inset-0 text-white/[0.04]">
          <GridPattern />
        </div>
        <div className="pointer-events-none absolute -top-24 -right-32 h-[28rem] w-[28rem] rounded-full bg-brand-500/25 blur-3xl" />
        <div className="pointer-events-none absolute top-40 -left-24 h-80 w-80 rounded-full bg-violet-500/20 blur-3xl" />
        <div className="relative mx-auto max-w-6xl px-4 pt-16 pb-12 sm:px-6 sm:pt-20">
          <p className="mb-3 animate-fade-up text-xs font-semibold uppercase tracking-wider text-brand-300">
            Integrations
          </p>
          <h1
            className="animate-fade-up text-4xl font-semibold tracking-tight text-white sm:text-5xl md:text-6xl"
            style={{ animationDelay: "60ms" }}
          >
            What ZopDesk <br className="hidden sm:block" />
            connects to today.
          </h1>
          <p
            className="mt-6 max-w-3xl animate-fade-up text-lg text-ink-300"
            style={{ animationDelay: "140ms" }}
          >
            Email, WhatsApp, phone, your store, your AI provider and your own
            systems. Each org connects its own accounts in Settings, and a REST
            API and webhooks cover anything custom.
          </p>
        </div>
      </section>


      <section className="border-y border-ink-200 bg-gradient-to-br from-white via-brand-50/40 to-white">
        <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6">
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { value: "3", label: "Channels", sub: "Email, WhatsApp and phone" },
              { value: "2", label: "Store sidebars", sub: "Shopify and Zopping" },
              { value: "2", label: "AI providers", sub: "OpenAI or Anthropic, your key" },
              { value: "30s", label: "Mailbox check", sub: "How often IMAP mailboxes are read" },
            ].map((s, i) => (
              <div
                key={s.label}
                className="animate-fade-up border-l-2 border-brand-500 pl-5"
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <p className="bg-gradient-to-br from-brand-600 to-violet-600 bg-clip-text text-4xl font-semibold tracking-tight text-transparent">
                  {s.value}
                </p>
                <p className="mt-2 text-sm font-medium text-ink-900">{s.label}</p>
                <p className="mt-0.5 text-xs text-ink-500">{s.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-20 sm:px-6">
        <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
          <div className="max-w-2xl">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-brand-700">
              The catalog
            </p>
            <h2 className="text-3xl font-semibold tracking-tight text-ink-900 sm:text-4xl">
              Everything that's available now.
            </h2>
            <p className="mt-3 text-ink-600">
              Live chat and Messenger/Instagram are on the roadmap and not
              listed here until they work.
            </p>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-100">
            <PlugIcon width={12} height={12} />
            Connected per org, in Settings
          </span>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {integrations.map(({ Mark, name, category }, i) => (
            <div
              key={name}
              className="group flex animate-fade-up items-start gap-4 rounded-xl border border-ink-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-brand-200 hover:shadow-md"
              style={{ animationDelay: `${i * 40}ms` }}
            >
              <Mark className="h-12 w-12 flex-none" />
              <div className="min-w-0">
                <p className="truncate text-base font-semibold text-ink-900">
                  {name}
                </p>
                <p className="mt-0.5 text-xs text-ink-500">{category}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="border-y border-ink-200 bg-gradient-to-br from-brand-50/60 via-white to-violet-50/50">
        <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6">
          <div className="mb-10 max-w-2xl">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-brand-700">
              How it works
            </p>
            <h2 className="text-3xl font-semibold tracking-tight text-ink-900 sm:text-4xl">
              Four things that hold for every integration.
            </h2>
          </div>
          <div className="grid gap-5 md:grid-cols-2">
            {capabilities.map(({ Icon, title, body }, i) => (
              <div
                key={title}
                className="animate-fade-up rounded-xl border border-ink-200 bg-white p-6 shadow-sm"
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-lg bg-gradient-to-br from-brand-500 to-violet-500 text-white shadow-md shadow-brand-500/20">
                  <Icon width={20} height={20} />
                </div>
                <h3 className="mb-2 text-base font-semibold text-ink-900">
                  {title}
                </h3>
                <p className="text-sm leading-relaxed text-ink-600">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-20 sm:px-6">
        <div className="grid gap-10 lg:grid-cols-[1fr_1.2fr] lg:items-center">
          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-brand-700">
              Your own systems
            </p>
            <h2 className="text-3xl font-semibold tracking-tight text-ink-900 sm:text-4xl">
              Connect ZopDesk to the tools you built yourself.
            </h2>
            <p className="mt-4 text-ink-600">
              Show your own customer data on tickets, add buttons that hand a
              ticket to your systems, and keep your tools in sync with the API
              and webhooks.
            </p>
            <ul className="mt-6 space-y-3 text-sm text-ink-700">
              {[
                "Customer data lookup: your fields and links in the ticket sidebar",
                "Ticket actions: up to 10 buttons, answer saved as an internal note",
                "API keys scoped to tickets:read, tickets:write and tickets:reply",
                "Webhooks signed with HMAC-SHA256, retried for about a day",
              ].map((p) => (
                <li key={p} className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex h-5 w-5 flex-none items-center justify-center rounded-full bg-brand-50 text-brand-700 ring-1 ring-brand-100">
                    <CheckIcon width={12} height={12} strokeWidth={2.4} />
                  </span>
                  {p}
                </li>
              ))}
            </ul>
          </div>
          <div className="overflow-hidden rounded-2xl border border-ink-200 bg-ink-900 text-ink-100 shadow-xl">
            <div className="flex items-center gap-2 border-b border-white/10 px-4 py-3 text-xs font-mono text-ink-300">
              <span className="h-2.5 w-2.5 rounded-full bg-red-400/70" />
              <span className="h-2.5 w-2.5 rounded-full bg-yellow-400/70" />
              <span className="h-2.5 w-2.5 rounded-full bg-green-400/70" />
              <span className="ml-3">GET /tickets</span>
            </div>
            <pre className="overflow-x-auto p-5 font-mono text-xs leading-relaxed">
{`curl https://api.zopdesk.com/tickets?status=open \\
  -H "Authorization: Bearer \${ZOPDESK_KEY}"

# webhook, signed with HMAC-SHA256
POST https://you.example.com/hooks
X-ZopDesk-Event: ticket.created
X-ZopDesk-Timestamp: 1760870000
X-ZopDesk-Signature: sha256=9f2a...
{
  "type": "ticket.created",
  "org": { "id": "org_ab12" },
  "data": {
    "ticket": { "id": "tkt_2048", "status": "open",
                "subject": "Refund request" }
  }
}`}
            </pre>
          </div>
        </div>
        <Link
          to="/developers"
          className="mt-8 inline-flex items-center gap-1 text-sm font-semibold text-brand-700 hover:text-brand-600"
        >
          Read the developer docs
          <ArrowRightIcon width={14} height={14} />
        </Link>
      </section>

      <section className="mx-auto max-w-6xl px-4 pb-20 sm:px-6">
        <div className="relative overflow-hidden rounded-3xl border border-ink-800 bg-ink-900 p-10 text-center shadow-xl sm:p-14">
          <div className="pointer-events-none absolute -top-32 left-1/2 h-[28rem] w-[28rem] -translate-x-1/2 rounded-full bg-brand-500/20 blur-3xl" />
          <div className="relative">
            <h2 className="text-3xl font-semibold tracking-tight text-white sm:text-4xl">
              Don't see your stack?
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-lg text-ink-300">
              Tell us what you use. What teams ask for decides what we build
              next.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link
                to="/contact"
                className="group inline-flex items-center gap-2 rounded-md bg-gradient-to-br from-brand-500 to-violet-500 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-brand-500/30 transition hover:from-brand-400 hover:to-violet-400 hover:shadow-xl hover:shadow-brand-500/40"
              >
                Talk to us
                <ArrowRightIcon
                  width={16}
                  height={16}
                  className="transition-transform group-hover:translate-x-0.5"
                />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
